import { actorId, definitionNotHeld, ecId, handBack, insertRoll, record, type JanusCtx, type Row, type StatusRow } from '../ability/context';
import { consentPending, TRIGGER_CONSENT_REASON } from '../ability/trigger-consent';
import { readItemField } from '../compile/item-field-reader';
import { statusConnectionPeers } from '../compile/creature-status-reader';
import { fromBaseUuid } from '../compile/from-base-context';
import { statusScalar } from '../actions/activity';
import { removeStatusFromRow, statusItemId, statusRemovalBefore, statusRows, STATUS_IDS } from '../statuses/expiry';
import type { DeathStatusExecutor } from './cleanup';
import { deathStatusRows } from './cleanup';
import { carrierHasTrigger } from '../ability/statuses/status-trigger-gate';
import { runTriggerFamily } from '../ability/trigger-executor';
import { browserDependencies } from '../port/browser-dependencies';
import { entries } from '../port/k4-dispatch/shared';
import type { DispatchEnv } from '../port/k4-dispatch/dispatch';
import { removeLinkedGrappleCounterpartStatus, removeLinkedRiddenPilotedCounterpartStatus } from '../port/k4-dispatch/status_removal_api';
import { encounterField } from '../../shared/array-types';
import { emptySpawnPlan, mergeSpawnPlans } from '../spawn/row-plan';

const value = (row: Row, key: string): string => String(statusScalar(row, key) ?? '').toUpperCase();

/** Physical retirement shared with upkeep's authoritatively departed targets, which have no live loss hooks. */
export function retireDeathStatus(ctx: JanusCtx, holders: readonly Row[], status: Row, added?: StatusRow): void {
    const id = value(status, 'status_id');
    added ??= ctx.statusesAdded.find(row => value(row as unknown as Row, 'status_id') === id);
    if (added) ctx.statusesAdded = ctx.statusesAdded.filter(row => row !== added);
    else if (value(status, 'status_target_id') === String(encounterField(ctx.encounter, 'encounter_id') ?? '').toUpperCase()) {
        const before = statusRemovalBefore(status, value(status, 'status_target_id'));
        const plan = emptySpawnPlan();
        plan.writes.push({ table: 'statuses', op: 'update', row: { status_id: id, status_deleted: 1 },
            before: { status_deleted: Number(before.status_deleted ?? 0), status_target_id: before.status_target_id,
                status_item_id: statusItemId(status), status_connected_id: String(before.status_connected_id ?? '') } });
        ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
    }
    else if (!ctx.statusesRemoved.some(row => row.status_id?.toUpperCase() === id))
        ctx.statusesRemoved.push({ status_id: id, status_item_id: statusItemId(status),
            target: value(status, 'status_target_id'), before: statusRemovalBefore(status, value(status, 'status_target_id')) });
    for (const holder of holders) removeStatusFromRow(holder, id);
}

/** Physical rows and hydrated projections have different lifetimes. In particular, the native
 * target remover detaches the primary before firing its loss hook, then retires the connection.
 * Hooks are required: callers cannot accidentally replace a special lifecycle with a raw delete. */
export interface DeathRemovalState {
    rows(): readonly Row[];
    target(id: string): Row | null;
    targetStatuses(id: string): readonly Row[];
    definition(status: Row): Row;
    detach(targetId: string, statusId: string): void;
    retire(status: Row): void;
    lost(target: Row, status: Row): void;
    formLost(target: Row, status: Row): void;
    specials(target: Row, status: Row): void;
    /** The all-target cascade runs only these specials, rather than the primary's full hook. */
    rottingRemoved(targetId: string): void;
    hasteRemoved(target: Row, status: Row): void;
    summonsRemoved(status: Row): void;
    concentrationBroken(targetId: string): void;
}

export type DeathRemovalEffects = Pick<DeathRemovalState,
    'formLost' | 'specials' | 'rottingRemoved' | 'hasteRemoved' | 'summonsRemoved'> & { beforeRetire?(target: Row | null, status: Row): void };

/** Browser adapter for the row executor. The physical ledger survives projection detachment,
 * and rows created by a loss hook join it before the next connected-row query. A pending row
 * removed in the same command is cancelled, rather than emitting a delete for an unsaved id. */
export function deathRemovalState(ctx: JanusCtx, holders: readonly Row[], effects: DeathRemovalEffects): DeathRemovalState {
    const targets = new Map(holders.map(row => [actorId(row), row]).filter(([id]) => !!id) as Array<[string, Row]>);
    const encounterId = String(encounterField(ctx.encounter, 'encounter_id') ?? '').toUpperCase();
    if (encounterId) targets.set(encounterId, ctx.encounter);
    const physical = new Map(deathStatusRows(holders).map(row => [value(row, 'status_id'), row]));
    const pending = new Map<string, StatusRow>();
    const removed = new Set<string>();
    const detached = new Set<string>();
    const rows = (): Row[] => {
        const additions = new Set(ctx.statusesAdded);
        for (const [id, added] of pending) if (!additions.has(added) && !detached.has(id)) {
            pending.delete(id); physical.delete(id);
            for (const holder of holders) removeStatusFromRow(holder, id);
        }
        for (const added of ctx.statusesAdded) {
            const row = added as unknown as Row;
            const statusId = value(row, 'status_id') || ctx.read.uuid();
            row.status_id = statusId;
            if (removed.has(statusId)) continue;
            pending.set(statusId, added);
            if (!physical.has(statusId)) physical.set(statusId, { ...row, status_id: statusId,
                status_target_id: added.target, status_caster_id: added.caster });
        }
        return [...physical.values()].filter(row => !ctx.statusesRemoved.some(removed =>
            removed.status_id ? removed.status_id.toUpperCase() === value(row, 'status_id')
                : removed.target.toUpperCase() === value(row, 'status_target_id') && removed.status_item_id.toUpperCase() === statusItemId(row)));
    };
    return {
        ...effects,
        rows,
        target: id => targets.get(id.toUpperCase()) ?? null,
        targetStatuses: id => {
            rows();
            const found = new Map(statusRows(targets.get(id.toUpperCase()) ?? null, 'target')
                .map(row => [value(row, 'status_id'), row]));
            for (const [statusId, row] of pending) if (row.target.toUpperCase() === id.toUpperCase()
                && !detached.has(statusId) && !removed.has(statusId)) found.set(statusId, physical.get(statusId)!);
            return [...found.values()];
        },
        definition: status => ctx.read.definition(statusItemId(status)) ?? record(status.status_arr)
            ?? definitionNotHeld('death:status-definition-not-held', statusItemId(status)),
        detach: (target, id) => {
            detached.add(id.toUpperCase());
            const added = pending.get(id.toUpperCase());
            if (added) ctx.statusesAdded = ctx.statusesAdded.filter(row => row !== added);
            const holder = targets.get(target.toUpperCase());
            if (holder) removeStatusFromRow(holder, id.toUpperCase());
        },
        retire: status => {
            const id = value(status, 'status_id');
            if (removed.has(id)) return;
            effects.beforeRetire?.(targets.get(value(status, 'status_target_id')) ?? null, status);
            removed.add(id); physical.delete(id);
            retireDeathStatus(ctx, holders, status, pending.get(id));
        },
        lost: (target, status) => {
            if (!ctx.members.has(ecId(target))) return;
            const event = { trigger: 'triggers_when_caster_loses_status_effect', statusItemId: statusItemId(status) };
            if (carrierHasTrigger(ctx, target, event.trigger, 'death:status-loss-trigger', event.statusItemId))
                runTriggerFamily(ctx, event, target, null, [target], false, undefined, true);
            if (!ctx.dryRun && consentPending(ctx.read)) handBack(`outcome:${TRIGGER_CONSENT_REASON}`);
        },
        concentrationBroken: target => insertRoll(ctx, { roll_type_1: 'concentration_broken', roll_item_id: STATUS_IDS.concentrating,
            by: targets.get(target) ?? null }),
    };
}

/** Native status_removal_api's death primitives. This owns selection, ordering, reciprocal
 * grapple retirement and one-peer/lone-source semantics. Effect hooks compose into the same plan. */
export function createDeathStatusExecutor(state: DeathRemovalState): DeathStatusExecutor & {
    removeSingle(targetId: string, statusId: string): void;
    removeExact(targetId: string, statusId: string, skipPeerCull?: boolean): void;
} {
    const visited = new Set<string>();
    const live = (id: string): Row | undefined => state.rows().find(row => value(row, 'status_id') === id.toUpperCase());
    const connection = (status: Row): Row[] => statusConnectionPeers(status, state.rows());
    const type = (status: Row): string => String(readItemField(state.definition(status), 'item_type') ?? '');
    const target = (status: Row): string => value(status, 'status_target_id');
    const retire = (status: Row): void => {
        if (!live(value(status, 'status_id'))) return;
        state.retire(status);
    };
    const member = (id: string): Row => {
        const row = state.target(id);
        if (!row) throw new Error(`death:status-target-not-held:${id}`);
        return row;
    };
    const selected = (targetId: string, statusId: string): Row | undefined => {
        const status = live(statusId);
        if (!status || target(status) !== targetId.toUpperCase()) return;
        // A stale caster copy must never resurrect a row removed from its target projection.
        return state.targetStatuses(targetId).some(row => value(row, 'status_id') === statusId.toUpperCase()) ? status : undefined;
    };
    const formLost = (holder: Row, status: Row): void => {
        if (type(status) === 'creature') state.formLost(holder, status);
    };
    const linked = browserDependencies({
        check_uuid: fromBaseUuid,
        item_field: (row: Row, key: string): unknown => key === 'item_id' ? statusItemId(row) : readItemField(state.definition(row), key),
        fetch_statuses: (filters: unknown): Row[] => state.rows().filter(row => entries(filters).every(([key, wanted]) =>
            key === 'status_deleted' ? Number(statusScalar(row, String(key)) ?? 0) === Number(wanted)
                : value(row, String(key)) === String(wanted ?? '').toUpperCase())),
        update_status_effect_stats: (row: Row): void => retire(row),
    }) as unknown as DispatchEnv;
    const specials = (holder: Row, status: Row): void => {
        // Reuse the existing ports: raw reciprocal rows have no independent loss/special hooks.
        removeLinkedRiddenPilotedCounterpartStatus(status, { value: [] }, linked);
        removeLinkedGrappleCounterpartStatus(status, { value: [] }, linked);
        state.specials(holder, status);
    };
    const peerAndLone = (status: Row): void => {
        const remaining = connection(status);
        if (!remaining.some(row => target(row) === target(status))) return;
        const item = statusItemId(status);
        const peer = state.targetStatuses(target(status)).find(row =>
            value(row, 'status_connected_id') === value(status, 'status_connected_id')
            && live(value(row, 'status_id'))
            && !(item === STATUS_IDS.restrained && [STATUS_IDS.grappled, STATUS_IDS.grappling].some(id => id === statusItemId(row)))
            && !(statusItemId(row) === STATUS_IDS.resting && item !== STATUS_IDS.resting)
            && !(type(row) === 'creature' && type(status) !== 'creature'));
        if (peer) retire(peer);
        const survivors = connection(status);
        if (survivors.length === 1 && statusItemId(survivors[0]!) === STATUS_IDS.concentrating) retire(survivors[0]!);
    };
    const removeConnection = (targetId: string, statusId: string): void => {
        const status = live(statusId);
        if (!status || target(status) !== targetId.toUpperCase() || !value(status, 'status_connected_id')) return;
        const rows = connection(status);
        for (const peer of rows) retire(peer);
        if (statusItemId(status) === '62E83DEB-4569-AE3A-F671-FF514C508FD2')
            for (const id of new Set(rows.map(target))) state.rottingRemoved(id);
        state.summonsRemoved(status);
        const fired = new Set<string>();
        for (const peer of rows) {
            const id = target(peer), key = `${id}:${statusItemId(peer)}`;
            if (fired.has(key)) continue;
            fired.add(key);
            const holder = state.target(id);
            // Encounter/inventory anchors have no creature loss hook.
            if (!holder) continue;
            state.lost(holder, peer);
            state.hasteRemoved(holder, peer);
        }
    };
    return {
        rows: state.rows,
        targetStatuses: state.targetStatuses,
        retire,
        forgetTargetStatus: state.detach,
        concentrationBroken: state.concentrationBroken,
        removeConnection,
        removeExact(targetId, statusId, skipPeerCull = false) {
            const status = selected(targetId, statusId);
            if (!status) return;
            const holder = member(targetId);
            formLost(holder, status);
            state.detach(targetId, statusId);
            state.lost(holder, status);
            retire(status);
            specials(holder, status);
            if (!skipPeerCull && connection(status).some(row => target(row) === target(status))) {
                const peer = state.targetStatuses(targetId).find(row =>
                    value(row, 'status_connected_id') === value(status, 'status_connected_id'));
                if (peer) retire(peer);
            }
        },
        removeTarget(targetId, statusId) {
            const normalized = statusId.toUpperCase();
            if (visited.has(normalized)) return;
            const status = selected(targetId, normalized);
            if (!status) return;
            visited.add(normalized);
            const holder = member(targetId);
            if (Number(statusScalar(status, 'status_aoe_source') ?? 0) === 1) {
                state.detach(targetId, normalized);
                removeConnection(targetId, normalized);
                return;
            }
            // Keep foreach order: a form whose origin names this primary can precede OR
            // follow it. Its cleanup belongs at that position, including after the strip.
            for (const peer of [...state.targetStatuses(targetId)]) {
                if (value(peer, 'status_id') === normalized || value(peer, 'status_origin_id') === normalized) formLost(holder, peer);
                if (value(peer, 'status_id') !== normalized) continue;
                state.detach(targetId, normalized);
                state.lost(holder, status);
                const peers = connection(status).filter(row => target(row) === target(status));
                for (const connected of peers) state.detach(targetId, value(connected, 'status_id'));
                for (const connected of peers) retire(connected);
                specials(holder, status);
                peerAndLone(status);
                state.summonsRemoved(status);
            }
        },
        removeSingle(targetId, statusId) {
            const status = selected(targetId, statusId);
            if (!status) return;
            if (Number(statusScalar(status, 'status_aoe_source') ?? 0) === 1) {
                state.detach(targetId, statusId);
                removeConnection(targetId, statusId);
                return;
            }
            const holder = member(targetId);
            formLost(holder, status);
            state.detach(targetId, statusId);
            state.lost(holder, status);
            retire(status);
            specials(holder, status);
            peerAndLone(status);
            state.summonsRemoved(status);
        },
    };
}
